import { useMemo } from 'react'
import { MutationHookOptions, QueryHookOptions } from '@apollo/client'
import {
  RateQuery,
  RateQueryVariables,
  RateUpdateMutation,
  RateUpdateMutationVariables,
  useRateDeleteMutation,
  useRateQuery,
  useRateUpdateMutation,
  RateDocument,
} from 'lib/graphql/storeadmin'
import {
  Rate as RateObject,
  RateVariant as RateVariantObject,
} from 'lib/graphql/storeadmin/types-and-hooks'
import { Options } from './interfaces'

export type {
  RateUpdateInput,
  RateVariantUpdateInput,
} from 'lib/graphql/storeadmin/types-and-hooks'

export type RateVariant = Omit<
  RateVariantObject,
  '__typename' | 'createdAt' | 'updatedAt'
>

export interface Rate
  extends Omit<
    RateObject,
    '__typename' | 'variants' | 'createdAt' | 'updatedAt'
  > {
  variants: Array<RateVariant>
}

type RateQueryHookOptions = QueryHookOptions<RateQuery, RateQueryVariables>
type RateMutationHookOptions = MutationHookOptions<
  RateUpdateMutation,
  RateUpdateMutationVariables
>

export type UseRateOptions = Options<
  RateQueryHookOptions,
  RateMutationHookOptions
>

type RateQueryReturnType = Omit<ReturnType<typeof useRateQuery>, 'data'>
type RateUpdateReturnType = ReturnType<typeof useRateUpdateMutation>
type RateDeleteReturnType = ReturnType<typeof useRateDeleteMutation>

export interface UseRateResult extends RateQueryReturnType {
  rate: Rate | null
  update: RateUpdateReturnType[0]
  updateResult: RateUpdateReturnType[1]
  remove: RateDeleteReturnType[0]
  removeResult: RateDeleteReturnType[1]
}

export function useRate(
  id: string,
  options: UseRateOptions = {}
): UseRateResult {
  const queryVariables = { id }
  const { data, refetch, ...queryResults } = useRateQuery({
    variables: queryVariables,
    onCompleted: options.onQueryCompleted,
    onError: options.onQueryError,
    context: options.context,
    pollInterval: options.pollInterval || 0,
    ssr: options.ssr || true,
  })
  const internalRate = useMemo(() => data?.rate, [data])
  const internalVariants = useMemo(
    () => internalRate?.variants?.edges?.map(({ variant }) => variant),
    [internalRate]
  )
  const rate = internalRate
    ? { ...internalRate, variants: internalVariants }
    : null

  const [update, updateResult] = useRateUpdateMutation({
    context: options.context,
    onCompleted: (v) => {
      options?.onMutationCompleted?.(v)
    },
    onError: options.onMutationError,
    onQueryUpdated: options.onQueryUpdated,
    refetchQueries: [{ query: RateDocument, variables: queryVariables }],
    awaitRefetchQueries: true,
  })

  const [remove, removeResult] = useRateDeleteMutation({
    context: options.context,
    onCompleted: (v) => {
      options?.onMutationCompleted?.(v)
    },
    onError: options.onMutationError,
    onQueryUpdated: options.onQueryUpdated,
  })

  return {
    rate,
    refetch,
    ...queryResults,
    update,
    updateResult,
    remove,
    removeResult,
  }
}
